import { SearchTextField } from "@/controls/quick-filter-textfield/SearchTextField";
import { useGridStyles } from "@/pages/laboratory/styles/gridStyles";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";

import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { useMaterialsOptionsFunctions } from "../hooks/useMaterialsOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { CreateMaterial } from "./CreateMaterial";
import { DeleteMaterial } from "./DeleteMaterial";
import { EditMaterial } from "./EditMaterial";

type Props = {
	getCatalogMaterialFromAPI: Function;
	isLoading: boolean;
};

export const TableMaterial = ({
	getCatalogMaterialFromAPI,
	isLoading,
}: Props) => {
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [materialId, setMaterialId] = useState<number | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [quickFilterText, setQuickFilterText] = useState("");

	const { getMenuOptions } = useMaterialsOptionsFunctions({
		setMaterialId,
		setShowEditModal,
		setShowDeleteModal,
		setShowCreateModal,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { rows, columnDefs } = useTableData({
		setMaterialId,
		setShowEditModal,
		setShowDeleteModal,
		showContextMenu,
	});

	const { columnDefaults, columnTypes } = useGridDefinitions({
		OnEdit: (data: any) => {
			setShowEditModal(!showEditModal);
			setMaterialId(data.Id);
		},
	});

	columnDefaults.floatingFilter = false;

	const onRowDoubleClicked = (event: any) => {
		setMaterialId(event.data.MaterialId);
		setShowEditModal(true);
	};

	const getContextMenuItems = React.useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "catMaterialsTable");
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "catMaterialsTable",
			getOptions: getMenuOptions,
		});
	}, []);

	const classes = useGridStyles();

	return (
		<>
			<Grid size={{ xs: 12 }} container justifyContent="center" spacing={1}>
				<Grid size={{ xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Materials
					</Typography>
				</Grid>

				<Grid
					size={{ xs: 12 }}
					container
					justifyContent="flex-end"
					alignContent="center"
				>
					<Grid size={{ xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search materials"
						/>
					</Grid>
					<Grid
						size={{ xs: 12 }}
						style={{ height: "68vh" }}
						className={classes.borderAgGridTable}
					>
						<div style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								rowData={rows || []}
								columnDefs={columnDefs}
								onGridReady={(params) => setGridApi(params.api)}
								loading={isLoading}
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								quickFilterText={quickFilterText}
								getRowId={(params) => params.data.MaterialId}
								suppressContextMenu
								rowSelection="single"
							/>
						</div>
					</Grid>

					<Grid size={{ xs: 12, md: "auto" }} style={{ marginTop: 10 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={{ minWidth: 250 }}
							onClick={() => setShowCreateModal(!showCreateModal)}
						>
							NEW MATERIAL
						</Button>
					</Grid>
				</Grid>
			</Grid>

			<CreateMaterial
				show={showCreateModal}
				onHide={(materialCreated) => {
					setShowCreateModal(false);
					setTimeout(() => {
						if (gridApi && materialCreated) {
							gridApi.paginationGoToLastPage();
						}
					}, 100);
				}}
				refreshData={getCatalogMaterialFromAPI}
			/>
			<EditMaterial
				show={showEditModal}
				onHide={(shouldUpdate?: boolean) => {
					setShowEditModal(false);
					setMaterialId(null);
					if (shouldUpdate) getCatalogMaterialFromAPI();
				}}
				MaterialId={materialId}
			/>
			<DeleteMaterial
				show={showDeleteModal}
				onHide={(shouldUpdate?: boolean) => {
					setShowDeleteModal(false);
					setMaterialId(null);
					if (shouldUpdate) getCatalogMaterialFromAPI();
				}}
				MaterialId={materialId}
			/>
		</>
	);
};
